import {
  useShellAgents,
  useShellDrawer,
  useShellSelection,
} from '../../provider';
import {
  useConversations,
  useDeleteConversation,
} from '../../provider/queries';
import { useMooChatContext } from '../../provider/internal-context';
import type { ConversationRef } from '../../types';
import { ConversationRow } from './ConversationRow';
import styles from './ShellDrawer.module.css';

const TOP_CONVERSATIONS_DEFAULT = 5;

function sortByRecency(a: ConversationRef, b: ConversationRef): number {
  return (b.updatedAt || '').localeCompare(a.updatedAt || '');
}

/** Default-view RECENT section: shows the first N conversations across all
 *  agents, with a "See all conversations" expander into the full in-drawer
 *  history view. */
export function ConversationsSection() {
  // Cross-agent list — fetched once per drawer open (TanStack cache).
  useConversations();
  const { state } = useMooChatContext();
  const agents = useShellAgents();
  const drawer = useShellDrawer();
  const { openConversation } = useShellSelection();
  const deleteMutation = useDeleteConversation();

  const all = Object.values(state.conversations.byId).sort(sortByRecency);
  const visible = all.slice(0, TOP_CONVERSATIONS_DEFAULT);

  return (
    <section className={styles.section} aria-label="Recent">
      <div className={styles.sectionHeader}>Recent</div>
      {visible.length === 0 ? (
        <div className={styles.empty}>No conversations yet.</div>
      ) : null}
      {visible.map((c) => (
        <ConversationRow
          key={c.id}
          ref_={c}
          agentName={agents.byId[c.agentId]?.name ?? ''}
          onOpen={() => openConversation(c.id, c.agentId)}
          onDelete={() => deleteMutation.mutate(c.id)}
        />
      ))}
      {/* Always visible (V1 drawer parity, OMNI-589): the expanded view is
          the only place to search or browse past the top N, and hiding the
          entry below N conversations read as "history is capped at 5" —
          notably for restricted personas whose only history surface this is. */}
      <button
        type="button"
        className={styles.moreRow}
        onClick={() => drawer.setView('all-conversations')}
      >
        See all conversations
      </button>
    </section>
  );
}
